import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';

import { getSupabase, type SupabaseBrowserClient } from './supabase';
import { makeApi, type ApiClient } from './api';

type Role = 'admin' | 'product' | 'project';

interface Profile {
  id: string;
  email: string | null;
  display_name: string | null;
  role: Role;
}

export type SessionStatus = 'loading' | 'authed' | 'unauthed';

interface SupabaseCtxValue {
  supabase: SupabaseBrowserClient;
  api: ApiClient;
  status: SessionStatus;
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  refresh: () => Promise<void>;
}

const SupabaseCtx = createContext<SupabaseCtxValue | null>(null);

/**
 * App-wide provider. Builds the typed Supabase client + api client once,
 * subscribes to auth-state changes, and loads the caller's profile row
 * (RLS lets every user read their own).
 */
export function SupabaseProvider({ children }: { children: ReactNode }) {
  const supabase = useMemo(() => getSupabase(), []);
  const api = useMemo(() => makeApi(supabase), [supabase]);

  const [status, setStatus] = useState<SessionStatus>('loading');
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);

  async function loadProfile(userId: string): Promise<Profile | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, display_name, role')
      .eq('id', userId)
      .single();
    if (error || !data) return null;
    return data as Profile;
  }

  async function applySession(next: Session | null) {
    setSession(next);
    if (!next) {
      setProfile(null);
      setStatus('unauthed');
      return;
    }
    const p = await loadProfile(next.user.id);
    setProfile(p);
    setStatus('authed');
  }

  useEffect(() => {
    let active = true;
    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (!active) return;
      await applySession(data.session);
    })();
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!active) return;
      void applySession(next);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
    // applySession + loadProfile close over `supabase` only; intentional run-once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabase]);

  const value: SupabaseCtxValue = {
    supabase,
    api,
    status,
    session,
    user: session?.user ?? null,
    profile,
    refresh: async () => {
      const { data } = await supabase.auth.getSession();
      await applySession(data.session);
    },
  };

  return <SupabaseCtx.Provider value={value}>{children}</SupabaseCtx.Provider>;
}

function useCtx(): SupabaseCtxValue {
  const ctx = useContext(SupabaseCtx);
  if (!ctx) throw new Error('useSupabase must be used inside <SupabaseProvider>');
  return ctx;
}

export function useSupabase(): SupabaseBrowserClient {
  return useCtx().supabase;
}

export function useApi(): ApiClient {
  return useCtx().api;
}

export interface UseSessionReturn {
  status: SessionStatus;
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  refresh: () => Promise<void>;
}

export function useSession(): UseSessionReturn {
  const { status, session, user, profile, refresh } = useCtx();
  return { status, session, user, profile, refresh };
}
